import { INSPECTION_STATUSES } from "@miplatform/constants";
import { IInspectionAttempt, InspectionActionTypes } from "@miplatform/types";
import {
  mdiCalendarBlankOutline,
  mdiCalendarRefreshOutline,
  mdiCancel,
  mdiFormatListBulleted,
  mdiTag,
  mdiTagOutline,
} from "@mdi/js";
import { Theme } from "@mui/material";

interface InspectionMenuItem {
  label: string;
  icon: string;
  action: () => void;
  color: string;
}

/**
 * Generates a list of menu items for an inspection attempt based on its current status.
 *
 * @param params - The parameters object
 * @param params.data - The inspection attempt data containing status and other details
 * @param params.onMenuItemClick - Optional callback function triggered when a menu item is clicked
 * @param params.theme - Material-UI theme object used for styling menu items
 *
 * @returns An array of menu item objects, each containing:
 * - `label`: Display text for the menu item
 * - `icon`: Material Design icon identifier
 * - `action`: Click handler function that invokes the onMenuItemClick callback
 * - `color`: Theme color for the menu item
 *
 * @remarks
 * The returned menu items vary based on the inspection attempt status:
 * - **SCHEDULED**: Returns Green Tag, Red Tag, and Reschedule options
 * - **READY**: Returns Schedule option
 * - **Other statuses**: Returns Details option
 */
export const getInspectionMenuItems = ({
  data,
  onMenuItemClick,
  theme,
}: {
  data: IInspectionAttempt;
  onMenuItemClick?: ({
    inspectionAttempt,
    actionType,
  }: {
    inspectionAttempt: IInspectionAttempt;
    actionType: InspectionActionTypes | null;
  }) => void;
  theme: Theme;
}) => {
  const greenTagAction = {
    label: "Green Tag",
    icon: mdiTagOutline,
    action: () =>
      onMenuItemClick?.({
        actionType: InspectionActionTypes.GreenTag,
        inspectionAttempt: data,
      }),
    color: theme.palette.success.main,
  };

  const redTagAction = {
    label: "Red Tag",
    icon: mdiTag,
    action: () =>
      onMenuItemClick?.({
        actionType: InspectionActionTypes.RedTag,
        inspectionAttempt: data,
      }),
    color: theme.palette.error.main,
  };

  const rescheduleAction = {
    label: "Reschedule",
    icon: mdiCalendarRefreshOutline,
    action: () =>
      onMenuItemClick?.({
        actionType: InspectionActionTypes.Reschedule,
        inspectionAttempt: data,
      }),
    color: theme.palette.info.main,
  };

  const scheduleAction = {
    label: "Schedule",
    icon: mdiCalendarBlankOutline,
    action: () =>
      onMenuItemClick?.({
        actionType: InspectionActionTypes.Schedule,
        inspectionAttempt: data,
      }),
    color: theme.palette.info.main,
  };

  const detailsAction = {
    label: "Details",
    icon: mdiFormatListBulleted,
    action: () =>
      onMenuItemClick?.({
        actionType: null,
        inspectionAttempt: data,
      }),
    color: theme.palette.grey.main,
  };

  const cancelAction = {
    label: "Cancel",
    icon: mdiCancel,
    action: () =>
      onMenuItemClick?.({
        actionType: InspectionActionTypes.Cancel,
        inspectionAttempt: data,
      }),
    color: theme.palette.error.main,
  };

  const menuItems: InspectionMenuItem[] = [];

  if (
    data.attemptStatus === INSPECTION_STATUSES.SCHEDULED ||
    data.attemptStatus === INSPECTION_STATUSES.PENDING
  ) {
    menuItems.push(
      detailsAction,
      rescheduleAction,
      greenTagAction,
      redTagAction,
      cancelAction
    );
  } else if (data.attemptStatus === INSPECTION_STATUSES.READY) {
    menuItems.push(
      detailsAction,
      scheduleAction,
      greenTagAction,
      redTagAction,
      cancelAction
    );
  } else {
    menuItems.push(detailsAction);
  }
  return menuItems;
};
